import { CampaignSchedule, FacebookAdError } from '.';
import { Business, exampleBusiness } from '../business/Business';
import { PixelEventType } from '../fb-pixel/PixelEventNames';
import { exampleUser, User } from '../user/User';
import { AdCreative, exampleAdCreative } from './AdCreative';
import { Adset } from './AdSet';
import { SavedAudience } from './audience/SavedAudience';
import { CampaignGoalType } from './CampaignGoalType';
import { PublisherPlatform } from './PublisherPlatform';
import { PlacementPositions } from '../facebook/audience/TargetingSpec/PlatformPlacements';
import { z } from 'zod';
import {
  AdObjectActiveStatus,
  EffectiveAdStatus,
  EffectiveCampaignStatus,
} from '../facebook/AdObjectStatus';
import {
  OptimizationGoal,
  OutcomeCampaignObjective,
} from '../facebook/AdDelivery';

export type Milliseconds = number;
export type Seconds = number;

export enum CampaignGoalName {
  InStoreSales = 'In-Store Sales',
  ECommerceSales = 'E-Commerce Sales',
  LeadGeneration = 'Lead Generation',
  WebsiteSessions = 'Website Sessions',
}

export enum CampaignGoalDefaultCostPer {
  InStoreSales = 27,
  ECommerceSales = 28,
  LeadGeneration = 29,
  WebsiteSessions = 30,
}

export declare type CampaignType = 'regular' | 'cascading';
export declare type CampaignUploadStatus =
  | 'pending'
  | 'completed'
  | 'errors'
  | 'warnings';

export interface AdData {
  id: number;
  name: string;
  adsetId: number;
  status: EffectiveAdStatus;
  // Active status simply refers to whether or not the ad is active/paused
  activeStatus?: AdObjectActiveStatus | null;
  startDate: number | null;
  errors?: FacebookAdError[];
  endDate: number | null;
  adGroup: number;
}

interface PerformanceData {
  spend?: number | null;
  kpi?: number | null;
  costPerCurrent?: number | null;
  roas?: number | null;
  revenue?: number | null;
}

interface RollingPerformanceData {
  rollingCostPer?: number | null;
}

export interface CampaignAd
  extends Partial<Omit<AdCreative, 'id' | 'status' | 'name'>>,
    AdData,
    PerformanceData {
  creativeID?: number;
}
export type AdGroup = (CampaignAd | undefined)[];

// prettier-ignore
export interface CampaignAdset extends Adset, Omit<SavedAudience, keyof Adset>, PerformanceData, RollingPerformanceData {
  adGroups?: AdGroup[]
}

export interface Campaign extends PerformanceData, RollingPerformanceData {
  id: number;
  // Status only exists if a campaign is published to facebook.
  // If a campaign fails to publish, the errors property will be populated INSTEAD of the status property.
  // 'Error' status is inferred via checking existance of the 'errors' property
  status?: EffectiveCampaignStatus | null;
  // Active status simply refers to whether or not the campaign is active/paused
  activeStatus?: AdObjectActiveStatus | null;

  // These are the Campaign Specific details, they match the DTO used.
  goal: CampaignGoalType;
  name: string;
  startTime: Seconds;
  endTime: Seconds;
  budget: number;
  publisherPlatforms?: PublisherPlatform[];
  isCascading: boolean;
  adjustmentSchedule?: CampaignSchedule;
  maxCostPer?: number;

  // These are the Adsets(built from audiences)
  // Each adset contains it's own 'adGroups' type.  Currently this is an exact copy of the Campaign['adGroups'] type
  // Eventually, these AdGroups will contain specific data relating to this Adset
  adSets?: CampaignAdset[];

  // These are extra analytics data, they will not exist until a campaign has started running.
  costPerIndustryAvg?: number | null;
  rank?: CampaignRank | null;

  // These Types Are Used For Retry/Errors functionality.
  config?: PublishCampaignDTO | null; // when retrying, this config is used as the DTO
  errors?: FacebookAdError[]; // when this property is populated, the FE shows errors and only allows retry
}

export interface CascadingCampaign {
  id: number;
  campaignID: number;
}

export declare type CreativeAdjustmentResult =
  | 'ADJUSTED'
  | 'NOT_ADJUSTED'
  | 'STOPPED'
  | 'NOT_READY_TO_EVALUATE';

export interface CampaignGoalKPI {
  kpiName: string;
  costPer: number;
}

export interface CampaignOptimizationAndKPIs {
  type: CampaignGoalType;
  enabled: boolean;
  kpi?: CampaignGoalKPI;
}

export interface CampaignGoals {
  optimizations: CampaignOptimizationAndKPIs[];
  cpm: number;
}

export interface CampaignRankDetailMetrics {
  topPercentile: number; // percent bracket
  rank: number;
}

export interface CampaignRankDetails {
  name: string;
  costPer: CampaignRankDetailMetrics;
  volume: CampaignRankDetailMetrics;
}

export const exampleCampaignRankDetails: CampaignRankDetails = {
  name: 'OTF',
  costPer: {
    topPercentile: 0.5,
    rank: 1,
  },
  volume: {
    topPercentile: 0.5,
    rank: 1,
  },
};

export interface CampaignRank {
  industry?: CampaignRankDetails;
  branch?: CampaignRankDetails;
}

export const exampleCampaignRank: CampaignRank = {
  branch: exampleCampaignRankDetails,
};

export interface CampaignConfig {
  name: string;
  budget: number;
  startTime: Seconds; // expects seconds
  endTime: Seconds; // expects seconds
  frequency: number;
  /**
   * The reporting bucket — funnel attribution, the ad-spend emails and the budget estimate's
   * cost-per all key off it. Set from the chosen optimization's `reportingGoal` on save; it no
   * longer decides anything about how the campaign is delivered. Absent on a draft whose AE has
   * not chosen an optimization yet.
   */
  goal?: CampaignGoalType;
  /**
   * What the campaign is delivered for, in Meta's own terms. Absent on every draft written
   * before the optimization catalog shipped, which normalizes through the legacy `goal` instead.
   * Both are set together or neither is.
   */
  objective?: OutcomeCampaignObjective;
  optimizationGoal?: OptimizationGoal;
  /**
   * The catalog row the AE picked. Needed because two rows can share an objective and performance
   * goal (Leads · Conversions and Leads · Website and Instant Forms differ only in
   * destination). Absent on drafts written before it existed, which resolve through the pair.
   */
  optimizationKey?: string;
  // optional DTO properties when creating campaigns
  pixelType?: PixelEventType;
  customConversionID?: number;
  // only used in cascading campaigns
  maxCostPer?: number;
}

export type CreativeGroup = (number | undefined)[];

export interface PublishCampaignDTO {
  campaign: CampaignConfig;
  audienceIDs: number[];
  publisherPlatforms?: PublisherPlatform[];
  /**
   * Manual placement positions within the selected platforms — the finer level under
   * `publisherPlatforms`, which still owns the platform choice on its own. Absent on every
   * draft written before placements shipped, which reads as "all positions" and is correct.
   */
  placementPositions?: PlacementPositions;
  spendLimitsByAudienceId?: {
    [audienceId: number]: { lifetimeMin?: number; lifetimeMax?: number };
  };
  creativeGroups: CreativeGroup[];
  sprintID?: number;
  /**
   * One-time instruction: create the campaign PAUSED so it can be reviewed before it spends.
   * Stripped from the stored config once the campaign publishes successfully.
   */
  publishPaused?: boolean;
}

export interface ExtendCampaignDTO {
  budget: number;
  end_time: string; // expects a ISO string
}

export interface IncreaseCampaignSpendDTO {
  increaseBudgetBy: number;
}

const pauseActionTypeSchema = z.union([
  z.literal('pause'),
  z.literal('resume'),
]);
export type CampaignPauseActionType = z.infer<typeof pauseActionTypeSchema>;

export const pauseCampaignSchema = z.object({
  action: pauseActionTypeSchema,
});

export type PauseCampaignDTO = z.TypeOf<typeof pauseCampaignSchema>;

export interface BulkUploadBusinessConfig {
  [businessID: number]: {
    budget: number;
    audienceIDs: number[];
    publisherPlatforms?: PublishCampaignDTO['publisherPlatforms'];
    spendLimitsByAudienceId?: PublishCampaignDTO['spendLimitsByAudienceId'];
  };
}
export interface BulkUploadCampaignDTO
  extends Omit<
    PublishCampaignDTO,
    | 'audienceIDs'
    | 'placementPositions'
    | 'spendLimitsByAudienceId'
    | 'campaign'
  > {
  bulkConfig: BulkUploadBusinessConfig;
  campaign: Omit<CampaignConfig, 'budget'>;
}

export const exampleCampaign: Campaign = {
  id: 1,
  status: 'ACTIVE',
  goal: CampaignGoalType.LeadGeneration,
  name: '',
  startTime: new Date('2020-10-15T14:04:39-0500').getTime(),
  endTime: new Date('2020-12-15T14:04:39-0500').getTime(),
  budget: 1000,
  spend: 200,
  kpi: 0,
  costPerCurrent: 0,
  costPerIndustryAvg: 0,
  rank: exampleCampaignRank,
  roas: 0,
  revenue: 0,
  isCascading: false,
};

export const examplePublishCampaignDTO: PublishCampaignDTO = {
  campaign: {
    name: 'New Campaign',
    budget: 200.0,
    startTime: new Date('2020-10-15T14:04:39-0500').getTime() / 1000,
    endTime: new Date('2020-11-15T14:04:39-0500').getTime() / 1000,
    goal: CampaignGoalType.LeadGeneration,
    frequency: 3,
  },
  audienceIDs: [1],
  creativeGroups: [],
};

export const exampleExtendCampaignDTO: ExtendCampaignDTO = {
  budget: 1000,
  end_time: '2020-12-15T15:05:40-0500',
};

export const exampleBulkUploadCampaign: BulkUploadCampaignDTO = {
  campaign: {
    name: 'New Bulk Campaign',
    startTime: new Date('2020-10-15T14:04:39-0500').getTime() / 1000,
    endTime: new Date('2020-11-15T14:04:39-0500').getTime() / 1000,
    goal: CampaignGoalType.LeadGeneration,
    frequency: 3,
  },
  bulkConfig: {
    1: {
      budget: 100,
      audienceIDs: [1, 2],
    },
    98: {
      budget: 250,
      audienceIDs: [1],
    },
  },
  creativeGroups: [],
};

export const exampleCampaignGoals: CampaignGoals = {
  cpm: 5,
  optimizations: [
    {
      type: CampaignGoalType.LeadGeneration,
      enabled: false,
    },
    {
      type: CampaignGoalType.WebsiteSessions,
      enabled: true,
      kpi: {
        costPer: 12,
        kpiName: 'Web Sessions',
      },
    },
    {
      type: CampaignGoalType.ECommerceSales,
      enabled: false,
    },
    {
      type: CampaignGoalType.InStoreSales,
      enabled: true,
      kpi: {
        costPer: 4.4,
        kpiName: 'Purchases',
      },
    },
  ],
};

export interface CampaignError {
  message: string;
  title?: string;
  details?: string;
}

export interface BusinessWithCampaign {
  campaign: Campaign;
  business: Business;
}

export interface BusinessWithCampaignErrors {
  business: Business;
  error: CampaignError;
}

export interface BulkCampaignUpload {
  created: BusinessWithCampaign[];
  issues: BusinessWithCampaignErrors[];
  creatives: AdCreative[];
  config: BulkUploadCampaignDTO;
  createdAt: number;
  updatedAt: number;
  user: User;
  status: CampaignUploadStatus;
}

export const bulkCampaignUpload: BulkCampaignUpload = {
  created: [
    {
      campaign: exampleCampaign,
      business: exampleBusiness,
    },
  ],
  issues: [
    {
      business: exampleBusiness,
      error: { message: 'failed to save to fb' },
    },
  ],
  creatives: [exampleAdCreative, exampleAdCreative],
  config: exampleBulkUploadCampaign,
  createdAt: 1603908720,
  updatedAt: 1603908720,
  user: exampleUser,
  status: 'errors',
};
